/* eslint-disable complexity */
import { RegItemFieldsProps } from 'src/routes/(registry)/list/[slug]/components/EditRegItemDrawer/fields/RegItemFields.types'
import Image from 'components/image'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Badge, IconButton, Textarea } from 'baby-design'
// eslint-disable-next-line import/no-extraneous-dependencies
import { EditPencil } from 'baby-design/icons'
import { RegItem } from 'src/types/regItem'
import { numberToCurrency } from 'lib/money'
import NiceModal from '@ebay/nice-modal-react'
import { Offer } from 'src/types/offer'
import EditItemDetailsPrivateItemSetting from 'src/routes/(registry)/list/[slug]/components/EditRegItemDrawer/components/PrivateItem/PrivateItem'
import { FC, ReactNode } from 'react'
import EditItemDetailsSecondhandItemSetting from 'src/routes/(registry)/list/[slug]/components/EditRegItemDrawer/components/SecondhandItem/SecondhandItem'
import useSecondhandItemsEnabled from 'src/routes/(registry)/list/[slug]/components/EditRegItemDrawer/hooks/useSecondhandItemsEnabled'
import { EditItemDetailsQuantitySetting } from '../components/QuantitySetting'
import { EditItemDetailsFavoriteSetting } from '../components/Favorite'
import { EditItemDetailsGroupGiftSetting } from '../components/GroupGift'

import css from './fields.style.scss'
import { CategoryDropdown } from '../components/CategoryDropdown'
import { EditItemDetailsBuyingOptions } from '../../BuyingOptionsWithAddButton'
import ProductInfoDrawer from '../../ProductInfoDrawer/ProductInfoDrawer'
import EnableGroupGiftModal from '../../EnableGroupGiftModal/EnableGroupGiftModal'
import EditRegItemDrawer from '../EditRegItemDrawer'
import AddSecondaryLinkDrawer from '../../AddSecondaryLinkDrawer/AddSecondaryLinkDrawer'
import EditSecondaryLinkDrawer from '../../EditSecondaryLinkDrawer/EditSecondaryLinkDrawer'
import RegItemPurchasesSection from './RegItemFields/components/RegItemPurchasesSection'

export function RegItemFields({
  regItem,
  categories,
  regItemFields,
  reservations,
  updateRegItemFields,
  clearRegItemFields,
  handleDelete,
  buyingOptionsProps = {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    unredeemedFundsCallback: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onAddToCartSuccess: () => {},
    toastImageOverride: null,
  },
  onRegItemUpdated,
}: RegItemFieldsProps) {
  const isSecondhandItemsEnabled = useSecondhandItemsEnabled()

  const openProductInfoDrawer = async () => {
    const response: RegItem | undefined = await NiceModal.show(
      ProductInfoDrawer,
      { regItem }
    )
    if (response) {
      NiceModal.show(EditRegItemDrawer, { regItem: response })
    }
  }

  const handleGroupGiftChange = async () => {
    const response: RegItem | undefined = await NiceModal.show(
      EnableGroupGiftModal,
      { regItem }
    )
    if (response) {
      clearRegItemFields('quantity')
      NiceModal.show(EditRegItemDrawer, { regItem: response })
    }
  }

  const openAddSecondaryLinkDrawer = async () => {
    const response: RegItem | undefined = await NiceModal.show(
      AddSecondaryLinkDrawer,
      { regItem }
    )
    if (response) {
      NiceModal.show(EditRegItemDrawer, { regItem: response })
    }
  }

  const openEditSecondaryLinkDrawer = async (offer: Offer) => {
    const response: RegItem | undefined = await NiceModal.show(
      EditSecondaryLinkDrawer,
      {
        secondaryOffer: offer,
        regItem,
      }
    )
    if (response) {
      NiceModal.show(EditRegItemDrawer, { regItem: response })
    }
  }

  const ImageBadge: FC<{ label: string }> = ({ label }) => (
    <Badge fill="standard-grey" label={label} size="small" variant="general" />
  )

  const quantityInCart = regItem.cartInfo?.quantity
  let badge: ReactNode
  if (quantityInCart && quantityInCart > 0) {
    badge = <ImageBadge label={`${quantityInCart} in cart`} />
  } else if (regItem.hasAtLeastOnePurchase && !regItem.isCrowdfund) {
    badge = <ImageBadge label="Purchased" />
  }

  return (
    <div className={css.fields}>
      <div className={css.fields__item_intro}>
        <div className={css.fields__item_intro_img_container}>
          <Image
            alt={regItemFields.title}
            className={css.fields__item_intro_img}
            src={regItemFields.imgUrl}
          />
          <div className={css.reg_item_card__img_badge}>{badge}</div>
        </div>
        <div className={css.fields__item_intro_info}>
          <div className={css.fields__item_title}>{regItemFields.title}</div>
          {regItemFields.price !== null && (
            <div className={css.fields__item_price}>
              {numberToCurrency(regItemFields.price)}
            </div>
          )}
        </div>
        <IconButton
          className={css.fields__image_edit}
          size="sm"
          variant="fill-inverted"
          onClick={openProductInfoDrawer}
        >
          <EditPencil />
        </IconButton>
      </div>
      <div className={css.fields__item_details}>
        <div className={css.fields__item_input}>
          <CategoryDropdown
            categories={categories}
            regItemFields={regItemFields}
            updateRegItemFields={updateRegItemFields}
          />
          <Textarea
            label="Notes to friends and family "
            placeholder="Let your gift givers know what size or color you would like"
            size="md"
            value={regItemFields.description || undefined}
            onChange={(e) =>
              updateRegItemFields({ description: e.target.value })
            }
          />
        </div>
        <EditItemDetailsQuantitySetting
          handleDelete={handleDelete}
          regItemFields={regItemFields}
          updateRegItemFields={updateRegItemFields}
        />
        <RegItemPurchasesSection
          regItem={regItemFields}
          reservations={reservations}
          onRegItemUpdated={onRegItemUpdated}
        />
        <EditItemDetailsFavoriteSetting
          regItemFields={regItemFields}
          updateRegItemFields={updateRegItemFields}
        />
        {!regItemFields.isGroupGift && (
          <EditItemDetailsPrivateItemSetting
            categories={categories}
            regItemFields={regItemFields}
            updateRegItemFields={updateRegItemFields}
          />
        )}
        {isSecondhandItemsEnabled && (
          <EditItemDetailsSecondhandItemSetting
            regItemFields={regItemFields}
            updateRegItemFields={updateRegItemFields}
          />
        )}
        {regItemFields.showGroupGiftToggle && (
          <EditItemDetailsGroupGiftSetting
            disabled={!regItem.canEnableGroupGift}
            handleGroupGiftChange={handleGroupGiftChange}
            regItemFields={regItemFields}
          />
        )}
        <EditItemDetailsBuyingOptions
          {...buyingOptionsProps}
          openAddSecondaryLinkDrawer={openAddSecondaryLinkDrawer}
          openEditSecondaryLinkDrawer={openEditSecondaryLinkDrawer}
          regItem={regItemFields}
          onCartItemUpdateSuccess={onRegItemUpdated}
        />
      </div>
      <div className={css.fields__divider} />
      <div className={css.fields__item_delete}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <a className={css.fields__delete_link} onClick={handleDelete}>
          Delete item
        </a>
      </div>
    </div>
  )
}
